import { format } from 'date-fns'
import * as XLSX from 'xlsx'

import { IForm } from 'core-components/classes/models/IForm'
import { sanitize } from 'core-components/helpers/sanitize'
import { multipleSort, singleSort } from 'core-components/types/helpers'

import { BaseFetcher } from './BaseFetcher'
import { IDataProviderFilter, IFilter } from './ICrudHelpers'
import { StaticFilterService } from './StaticFilterService'
import {
  ICrudImplementation,
  IPagingFromTo,
  IQueryResult,
  TCreateFunction,
  TDeleteFunction,
  TEnrichReadData,
  TFetcher,
  TSyncCache,
  TUpdateFunction,
} from './types'

type TFunctions<T> = {
  create?: TCreateFunction<T>
  update?: TUpdateFunction<T>
  enrichReadData?: TEnrichReadData<T>
}

export class Static<T = IForm, R = string | number>
  extends BaseFetcher<T>
  implements ICrudImplementation<T, R>
{
  private data: T[] = []
  private readonly functions?: TFunctions<T>

  constructor(data: T[], functions?: TFunctions<T>) {
    super()
    this.data = data
    this.functions = functions
  }

  public getInitialProps = (): any => {
    return { data: this.data, functions: this.functions }
  }

  public read: TFetcher<T, R> = async (
    query,
    filter,
    sort,
    paging,
    keyField,
    fetchCallBack,
    hierarchyField,
    expandedWithoutIgnored,
    hierarchyRoot
  ): Promise<IQueryResult<T, R>> => {
    if (this.functions?.enrichReadData) {
      this.data = await this.functions.enrichReadData(this.data, keyField)
    }

    const newPaging = paging as IPagingFromTo
    const filteredWithHierarchy =
      filter.filters.length >= 1 && hierarchyField
        ? this.filterWithHierarchy(filter, keyField, hierarchyField, hierarchyRoot)
        : undefined
    let filtered: T[]
    if (filteredWithHierarchy) {
      filtered = filteredWithHierarchy.data
    } else if (filter.filters.length >= 1 && !hierarchyField) {
      const parentFilter = {
        resultData: [],
        ...filter,
        availableData: [...this.data],
      }
      StaticFilterService.filterData(filter.filters, [...this.data], parentFilter)
      filtered = parentFilter.resultData
    } else {
      filtered = [...this.data]
    }

    let paged
    if (sort) {
      paged = filtered.sort((a, b) => {
        if (Array.isArray(sort)) {
          return multipleSort<T>({ firstItem: a, secondItem: b, sortData: sort })
        }

        return singleSort<T>({ firstItem: a, secondItem: b, sortData: sort })
      })
    } else {
      paged = filtered
    }

    if (paging) {
      paged = paged.slice(
        newPaging.from,
        newPaging.from === newPaging.to ? newPaging.from + 1 : newPaging.to
      )
    }
    return {
      items: paged,
      matched: filteredWithHierarchy?.matched || undefined,
      total: 0,
    }
  }
  public create: TCreateFunction<T> = async (formId, models): Promise<T[]> => {
    if (this.functions?.create) {
      return this.functions.create(formId, models)
    }
    models.forEach((m) => {
      this.data.push(m as T)
    })

    return models as T[]
  }

  public updateItem: TSyncCache<T> = ({ model, keyField }) => {
    if (!keyField) {
      return console.error('Types.Static: You must set keyField for updateItem method')
    }

    let isExits = false
    this.data = this.data.map((el) => {
      if (el[keyField] === model[keyField]) {
        isExits = true
        return model
      }
      return el
    })

    if (!isExits) {
      this.data = [model, ...this.data]
    }
  }

  public deleteItem: TSyncCache<T> = ({ model, keyField }) => {
    if (!keyField) {
      return console.error('Types.Static: You must set keyField for deleteItem method')
    }

    this.data = this.data.filter((el) => el[keyField] !== model[keyField])
  }

  public update: TUpdateFunction<T> = async (formId, models, keyField): Promise<T[]> => {
    if (this.functions?.update) {
      return this.functions.update(formId, models)
    }
    if (keyField) {
      models.forEach((m) => {
        const key = m[keyField]
        const finded: any = this.data.find((d: any) => {
          return d[keyField] === key
        })
        if (finded) {
          const keys = Object.keys(m)
          keys.forEach((oneKey: any) => {
            finded[oneKey] = m[oneKey]
          })
        }
      })
    }
    return []
  }

  public delete: TDeleteFunction = async (formId, id, keyField): Promise<boolean> => {
    if (keyField) {
      const findedIndex = this.data.findIndex((d: any) => d[keyField] === id)
      if (findedIndex > -1) {
        this.data.splice(findedIndex, 1)
      }
    }
    return true
  }

  public getStaticData = () => {
    return this.data
  }

  private findParents = (
    el: T,
    filters: IFilter[],
    parents: T[],
    keyField: string,
    hierarchyField: string,
    hierarchyRoot?: R | null
  ): T[] => {
    const isRoot = hierarchyField ? el[hierarchyField] === hierarchyRoot : false
    if (isRoot) {
      return parents
    }

    const parent = this.data.find((element) => {
      if (hierarchyField && keyField) {
        // @ts-ignore
        return el[hierarchyField] === element[keyField]
      }
      return false
    })

    // @ts-ignore
    if (parent && !parents.find((el) => el[keyField] === parent[keyField])) {
      return this.findParents(
        parent,
        filters,
        [...parents, parent],
        keyField,
        hierarchyField,
        hierarchyRoot
      )
    }
    return parents
  }

  private filterWithHierarchy = (
    filter: IDataProviderFilter,
    keyField: string,
    hierarchyField: string,
    hierarchyRoot?: R | null
  ) => {
    let matched = {} as T
    let newData = [] as T[]
    this.data.forEach((el) => {
      let isSelected = StaticFilterService.matchFilter(el, filter)
      if (isSelected) {
        // @ts-ignore
        matched = keyField ? { ...matched, [el[keyField]]: el } : matched
        const parents = this.findParents(
          el,
          filter.filters as IFilter[],
          [el],
          keyField,
          hierarchyField,
          hierarchyRoot
        )
        if (parents.length === 0) {
          isSelected = false
        }
        if (isSelected) {
          newData = [...newData, ...parents]
        }
      }
    })

    // @ts-ignore
    const keyIds = newData.map((mapObj) => mapObj[keyField])
    return {
      matched,
      data: newData.filter((obj: T, pos: number) => {
        // @ts-ignore
        return keyIds.indexOf(obj[keyField]) === pos
      }),
    }
  }

  public async export(
    filter: IDataProviderFilter,
    _?: string,
    columns?: { title: string; name: string }[]
  ) {
    if (!columns) {
      console.error(sanitize('You must provide columns for static fetcher'))
      return { Url: '', Blob: undefined }
    }

    let iterationData: T[] = this.data

    if (filter && filter.filters.length > 0) {
      const parentFilter = {
        resultData: [],
        ...filter,
        availableData: [...this.data],
      }
      StaticFilterService.filterData(filter.filters, [...this.data], parentFilter)
      iterationData = parentFilter.resultData
    }

    const data = iterationData.map((item) => {
      const row: Record<string, string | number> = {}
      for (const col of columns) {
        row[col.title] = item[col.name]
      }
      return row
    })

    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    })

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    return { Url: '', Blob: blob }
  }

  public clone(): Static<T, R> {
    return new Static(this.data, this.functions)
  }
}
